import { useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  ALargeSmall,
  Baseline,
  Eraser,
  List,
  ListOrdered,
  IndentIncrease,
  IndentDecrease,
  Undo2,
  Redo2,
  Maximize2,
  Minimize2,
} from "lucide-react";

const SIZE_OPTIONS = [
  { label: "Nhỏ", value: "12px" },
  { label: "Vừa", value: "14px" },
  { label: "Lớn", value: "18px" },
  { label: "Rất lớn", value: "24px" },
];

const COLOR_SWATCHES = [
  "#000000", "#5c5c5c", "#8a8a8a", "#b5b5b5",
  "#ff3b30", "#ff9500", "#ffcc00", "#34c759",
  "#00c7be", "#30b0c7", "#007aff", "#5856d6",
  "#af52de", "#ff2d55", "#a2845e", "#ffffff",
];

function ToolButton({ icon: Icon, title, onMouseDown, active, className = "" }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onMouseDown?.(e);
      }}
      title={title}
      aria-label={title}
      aria-pressed={active || undefined}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? "bg-blue-50 text-blue-600"
          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      } ${className}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-5 w-px bg-gray-200" />;
}

function Popover({ trigger, children, align = "top" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-flex">
      <span onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((p) => !p)}>
        {trigger}
      </span>
      {open && (
        <div
          className={`absolute ${
            align === "top" ? "bottom-full mb-1" : "top-full mt-1"
          } left-0 z-50 bg-white rounded-md shadow-lg ring-1 ring-black/10 p-2`}
          onMouseDown={(e) => e.preventDefault()}
        >
          {typeof children === "function" ? children({ close: () => setOpen(false) }) : children}
        </div>
      )}
    </div>
  );
}

export default function FormatToolbar({
  activeFormats,
  onCommand,
  onToggleExpand,
  isExpanded,
}) {
  const isActive = (k) => activeFormats?.has(k);

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 border-t border-gray-100 bg-white flex-wrap">
      <ToolButton
        icon={Bold}
        title="Đậm (Cmd+B)"
        active={isActive("bold")}
        onMouseDown={() => onCommand("bold")}
      />
      <ToolButton
        icon={Italic}
        title="Nghiêng (Cmd+I)"
        active={isActive("italic")}
        onMouseDown={() => onCommand("italic")}
      />
      <ToolButton
        icon={Underline}
        title="Gạch chân (Cmd+U)"
        active={isActive("underline")}
        onMouseDown={() => onCommand("underline")}
      />
      <ToolButton
        icon={Strikethrough}
        title="Gạch ngang (Cmd+Shift+S)"
        active={isActive("strikeThrough")}
        onMouseDown={() => onCommand("strikeThrough")}
      />

      <Popover
        trigger={
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            title="Cỡ chữ"
            className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          >
            <ALargeSmall className="w-4 h-4" />
          </button>
        }
      >
        {({ close }) => (
          <div className="flex flex-col gap-0.5 min-w-[110px]">
            {SIZE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onCommand("fontSize", opt.value);
                  close();
                }}
                className="text-left px-2 py-1 text-sm rounded hover:bg-gray-100"
                style={{ fontSize: opt.value }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </Popover>

      <Popover
        trigger={
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            title="Màu chữ"
            className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          >
            <Baseline className="w-4 h-4" />
          </button>
        }
      >
        {({ close }) => (
          <div className="grid grid-cols-4 gap-1">
            {COLOR_SWATCHES.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onCommand("foreColor", c);
                  close();
                }}
                className="w-6 h-6 rounded ring-1 ring-black/10 hover:scale-110 transition-transform"
                style={{ backgroundColor: c }}
                aria-label={`Màu ${c}`}
                title={c}
              />
            ))}
          </div>
        )}
      </Popover>

      <ToolButton
        icon={Eraser}
        title="Xóa định dạng"
        onMouseDown={() => onCommand("removeFormat")}
      />

      <Divider />

      <ToolButton
        icon={List}
        title="Danh sách chấm"
        active={isActive("insertUnorderedList")}
        onMouseDown={() => onCommand("insertUnorderedList")}
      />
      <ToolButton
        icon={ListOrdered}
        title="Danh sách số"
        active={isActive("insertOrderedList")}
        onMouseDown={() => onCommand("insertOrderedList")}
      />
      <ToolButton
        icon={IndentIncrease}
        title="Tăng thụt lề"
        onMouseDown={() => onCommand("indent")}
      />
      <ToolButton
        icon={IndentDecrease}
        title="Giảm thụt lề"
        onMouseDown={() => onCommand("outdent")}
      />

      <Divider />

      <ToolButton
        icon={Undo2}
        title="Hoàn tác (Cmd+Z)"
        onMouseDown={() => onCommand("undo")}
      />
      <ToolButton
        icon={Redo2}
        title="Làm lại (Cmd+Shift+Z)"
        onMouseDown={() => onCommand("redo")}
      />

      <div className="ml-auto">
        <ToolButton
          icon={isExpanded ? Minimize2 : Maximize2}
          title={isExpanded ? "Thu nhỏ" : "Mở rộng"}
          onMouseDown={onToggleExpand}
        />
      </div>
    </div>
  );
}
